import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Service } from '@/api/services/servicesApi.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { Icon } from '@/components/common/Icon';
import { useSaveServiceMutation } from '@/hooks/useServiceMutations';
import {
  buildTherapyTypeTranslationKey,
  THERAPY_TYPE_CODE_OPTIONS,
} from '@/constants/appointmentOptions';
import {
  convertMajorAmountToMinorUnits,
  formatMinorUnitsAsEditableAmount,
} from '@/utils/moneyFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

const NO_THERAPY_TYPE = '';
const DEFAULT_CURRENCY_PLACEHOLDER = 'PLN';
const CURRENCY_CODE_PATTERN = /^[A-Za-z]{3}$/;

interface ServiceFormModalProps {
  service: Service | null;
  onClose: () => void;
}

export function ServiceFormModal({ service, onClose }: ServiceFormModalProps) {
  const { t, i18n } = useTranslation('services');
  const isEditing = service !== null;
  const mutation = useSaveServiceMutation();

  const [nameEn, setNameEn] = useState(service === null ? '' : service.nameEn);
  const [namePl, setNamePl] = useState(service === null ? '' : service.namePl);
  const [descriptionEn, setDescriptionEn] = useState(
    service === null || service.descriptionEn === null ? '' : service.descriptionEn
  );
  const [descriptionPl, setDescriptionPl] = useState(
    service === null || service.descriptionPl === null ? '' : service.descriptionPl
  );
  const [preparationEn, setPreparationEn] = useState(
    service === null || service.preparationEn === null ? '' : service.preparationEn
  );
  const [preparationPl, setPreparationPl] = useState(
    service === null || service.preparationPl === null ? '' : service.preparationPl
  );
  const [currency, setCurrency] = useState(service === null ? '' : service.currency);
  const [priceText, setPriceText] = useState(
    service === null
      ? ''
      : formatMinorUnitsAsEditableAmount(
          service.priceMinorUnits,
          service.currency,
          i18n.language
        )
  );
  const [therapyType, setTherapyType] = useState(
    service === null || service.therapyType === null ? NO_THERAPY_TYPE : service.therapyType
  );
  const [isActive, setIsActive] = useState(service === null ? true : service.isActive);
  const [showErrors, setShowErrors] = useState(false);

  const nameEnMissing = nameEn.trim().length === 0;
  const namePlMissing = namePl.trim().length === 0;
  const trimmedCurrency = currency.trim().toUpperCase();
  const currencyInvalid = trimmedCurrency.length > 0 && !CURRENCY_CODE_PATTERN.test(trimmedCurrency);
  const trimmedPriceText = priceText.trim();
  const parsedPrice = trimmedPriceText.length === 0 ? null : Number(trimmedPriceText);
  const priceInvalid =
    trimmedPriceText.length > 0 && (Number.isNaN(parsedPrice) || (parsedPrice !== null && parsedPrice < 0));

  async function save() {
    if (nameEnMissing || namePlMissing || currencyInvalid || priceInvalid) {
      setShowErrors(true);
      return;
    }
    try {
      const priceMinorUnits =
        parsedPrice === null
          ? null
          : convertMajorAmountToMinorUnits(
              parsedPrice,
              trimmedCurrency.length === 0 ? DEFAULT_CURRENCY_PLACEHOLDER : trimmedCurrency,
              i18n.language
            );
      await mutation.mutateAsync({
        serviceId: service === null ? null : service.id,
        payload: {
          nameEn: nameEn.trim(),
          namePl: namePl.trim(),
          descriptionEn: descriptionEn.trim().length === 0 ? null : descriptionEn.trim(),
          descriptionPl: descriptionPl.trim().length === 0 ? null : descriptionPl.trim(),
          preparationEn: preparationEn.trim().length === 0 ? null : preparationEn.trim(),
          preparationPl: preparationPl.trim().length === 0 ? null : preparationPl.trim(),
          priceMinorUnits,
          currency: trimmedCurrency.length === 0 ? null : trimmedCurrency,
          therapyType: therapyType === NO_THERAPY_TYPE ? null : therapyType,
          isActive,
          sortOrder: service === null ? 0 : service.sortOrder,
        },
      });
      showSuccessNotification(isEditing ? t('form.updated') : t('form.saved'));
      onClose();
    } catch (error) {
      showErrorNotification(resolveApiErrorMessage(error));
    }
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <div className="bg-zoowhite w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-5 border-b border-zooborder flex justify-between items-center bg-zoovpalelavender/40">
          <h3 className="text-lg font-bold text-zooindigo">
            {isEditing ? t('form.editTitle') : t('form.addTitle')}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('form.cancel')}
            onClick={onClose}
            className="rounded-full"
          >
            <Icon name="X" className="text-zooindigo" />
          </Button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="service-name-en">
                {t('form.nameEn')} <span className="text-zooerrortext">*</span>
              </Label>
              <Input
                id="service-name-en"
                value={nameEn}
                hasError={showErrors && nameEnMissing}
                placeholder={t('form.nameEnPlaceholder')}
                onChange={function update(changeEvent) {
                  setNameEn(changeEvent.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="service-name-pl">
                {t('form.namePl')} <span className="text-zooerrortext">*</span>
              </Label>
              <Input
                id="service-name-pl"
                value={namePl}
                hasError={showErrors && namePlMissing}
                placeholder={t('form.namePlPlaceholder')}
                onChange={function update(changeEvent) {
                  setNamePl(changeEvent.target.value);
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="service-description-en">{t('form.descriptionEn')}</Label>
              <Textarea
                rows={2}
                id="service-description-en"
                value={descriptionEn}
                placeholder={t('form.descriptionPlaceholder')}
                onChange={function update(changeEvent) {
                  setDescriptionEn(changeEvent.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="service-description-pl">{t('form.descriptionPl')}</Label>
              <Textarea
                rows={2}
                id="service-description-pl"
                value={descriptionPl}
                placeholder={t('form.descriptionPlaceholder')}
                onChange={function update(changeEvent) {
                  setDescriptionPl(changeEvent.target.value);
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="service-preparation-en">{t('form.preparationEn')}</Label>
              <Textarea
                rows={2}
                id="service-preparation-en"
                value={preparationEn}
                placeholder={t('form.preparationPlaceholder')}
                onChange={function update(changeEvent) {
                  setPreparationEn(changeEvent.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="service-preparation-pl">{t('form.preparationPl')}</Label>
              <Textarea
                rows={2}
                id="service-preparation-pl"
                value={preparationPl}
                placeholder={t('form.preparationPlaceholder')}
                onChange={function update(changeEvent) {
                  setPreparationPl(changeEvent.target.value);
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="service-price">{t('form.price')}</Label>
              <Input
                type="text"
                inputMode="decimal"
                id="service-price"
                value={priceText}
                hasError={showErrors && priceInvalid}
                placeholder={t('form.pricePlaceholder')}
                onChange={function update(changeEvent) {
                  setPriceText(changeEvent.target.value);
                }}
              />
              <p className="text-xs text-zoomuted">{t('form.priceHint')}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="service-currency">{t('form.currency')}</Label>
              <Input
                id="service-currency"
                value={currency}
                hasError={showErrors && currencyInvalid}
                placeholder={t('form.currencyPlaceholder')}
                onChange={function update(changeEvent) {
                  setCurrency(changeEvent.target.value.toUpperCase());
                }}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="service-therapy-type">{t('form.therapyType')}</Label>
            <SearchableSelect
              inputId="service-therapy-type"
              value={therapyType}
              placeholder={t('form.therapyTypeNone')}
              isClearable
              options={THERAPY_TYPE_CODE_OPTIONS.map(function toOption(code) {
                return { value: code, label: t(buildTherapyTypeTranslationKey(code)) };
              })}
              onChange={setTherapyType}
            />
          </div>
        </div>

        <div className="p-5 border-t border-zooborder flex items-center justify-between bg-zoooffwhite">
          <label className="flex items-center gap-2.5 text-[13px] font-semibold text-zooindigo">
            <ToggleSwitch
              isChecked={isActive}
              onToggle={function toggle() {
                setIsActive(function invert(previous) {
                  return previous === false;
                });
              }}
              accessibleLabel={t('form.activeLabel')}
            />
            {t('form.activeLabel')}
          </label>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('form.cancel')}
            </Button>
            <Button type="button" isLoading={mutation.isPending} onClick={save}>
              {t('form.save')}
            </Button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}
